"use client"

import { useCallback, useState } from "react"

import { NavigationItem } from "../../MainNavigation/type"

export interface MobileMenuState {
  isMobileMenuOpen: boolean
  isMobileSearchOpen: boolean
  expandedMenuKey: string | null
  selectedThirdLevelMenu: { parentTitle: string; item: NavigationItem } | null
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  toggleMenuExpansion: (key: string) => void
  handleSecondLevelClick: (parentTitle: string, item: NavigationItem) => void
  handleBackToSecondLevel: () => void
  toggleMobileSearch: () => void
  setIsMobileSearchOpen: (open: boolean) => void
}

/**
 * 移动端菜单状态管理 Hook
 */
export function useMobileMenu(): MobileMenuState {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false)
  const [expandedMenuKey, setExpandedMenuKey] = useState<string | null>(null)
  const [selectedThirdLevelMenu, setSelectedThirdLevelMenu] = useState<{
    parentTitle: string
    item: NavigationItem
  } | null>(null)

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen((prev) => {
      if (prev) {
        // 关闭菜单时重置所有状态
        setExpandedMenuKey(null)
        setSelectedThirdLevelMenu(null)
      } else {
        // 打开菜单时关闭搜索框
        setIsMobileSearchOpen(false)
      }
      return !prev
    })
  }, [])

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
    setExpandedMenuKey(null)
    setSelectedThirdLevelMenu(null)
  }, [])

  const toggleMenuExpansion = useCallback((key: string) => {
    setExpandedMenuKey((prev) => {
      const newKey = prev === key ? null : key
      // 展开新菜单时，关闭三级菜单
      if (newKey !== prev) {
        setSelectedThirdLevelMenu(null)
      }
      return newKey
    })
  }, [])

  const handleSecondLevelClick = useCallback(
    (parentTitle: string, item: NavigationItem) => {
      if (item.children && item.children.length > 0) {
        // 如果有三级菜单，显示三级菜单页面
        setSelectedThirdLevelMenu({ parentTitle, item })
      } else {
        // 如果没有三级菜单，直接跳转
        closeMobileMenu()
      }
    },
    [closeMobileMenu]
  )

  const handleBackToSecondLevel = useCallback(() => {
    setSelectedThirdLevelMenu(null)
  }, [])

  const toggleMobileSearch = useCallback(() => {
    setIsMobileSearchOpen((prev) => {
      if (!prev) {
        // 打开搜索时关闭菜单
        setIsMobileMenuOpen(false)
        setExpandedMenuKey(null)
        setSelectedThirdLevelMenu(null)
      }
      return !prev
    })
  }, [])

  return {
    isMobileMenuOpen,
    isMobileSearchOpen,
    expandedMenuKey,
    selectedThirdLevelMenu,
    toggleMobileMenu,
    closeMobileMenu,
    toggleMenuExpansion,
    handleSecondLevelClick,
    handleBackToSecondLevel,
    toggleMobileSearch,
    setIsMobileSearchOpen,
  }
}
